import { useState, useEffect } from 'react';

/**
 * Hook para cargar configuración dinámica del tenant desde /api/tenant-config
 * Transforma la respuesta del backend al formato esperado por el frontend
 */
export function useTenantConfig() {
    const [config, setConfig] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        async function fetchConfig() {
            try {
                const response = await fetch('/api/tenant-config');
                const data = await response.json();

                if (data.status === 'ok') {
                    console.log('✅ Tenant config loaded:', data.tenant_id);
                    const transformedConfig = transformBackendConfig(data.config, data.tenant_id);
                    setConfig(transformedConfig);
                } else {
                    throw new Error(data.message || 'Error loading config');
                }
            } catch (err) {
                console.error('❌ Error fetching tenant config:', err);
                setError(err);
                // No establecer config aquí - dejar que App.js use su fallback
            } finally {
                setLoading(false);
            }
        }

        fetchConfig();
    }, []);

    return { config, loading, error };
}

/**
 * Transforma config.py (NEGOCIO) del backend al formato esperado por frontend
 */
function transformBackendConfig(backendConfig, tenantId) {
    // Extraer nombre distintivo (quita "Barbería" para dejar solo el nombre)
    const rawName = backendConfig.nombre || 'Barbería Demo';
    const nombreCorto = rawName.replace(/Barbería/gi, '').trim() || rawName;

    return {
        branding: {
            nombre: nombreCorto.toUpperCase(),
            nombreCompleto: backendConfig.nombre || 'Barbería Demo',
            colores: {
                primario: '#f59e0b',
                secundario: '#27272a',
                acento: '#f59e0b'
            },
            fuente: 'Oswald'
        },
        contacto: {
            direccion: backendConfig.direccion || 'Dirección no configurada',
            whatsapp: backendConfig.telefono || null,
            telegram: backendConfig.telegram || null,
            instagram: null,
            coords: { lat: 43.3209, lng: -3.0186, zoom: 15 }
        },
        servicios: (backendConfig.servicios || []).map(s => ({
            id: s.nombre.toLowerCase().replace(/ /g, '-'),
            nombre: s.nombre,
            precio: s.precio,
            precioHabitual: s.precioHabitual || s.precio,
            duracion: 30,
            descripcion: `Servicio de ${s.nombre}`
        })),
        fidelidad: backendConfig.fidelidad ? {
            activo: backendConfig.fidelidad.activo !== false,
            visitasParaPremio: backendConfig.fidelidad.visitasParaPremio || 10,
            descuentoHabitual: 2.00,
            textoDescuento: '¡Cliente habitual! Descuento aplicado.',
            textoPremio: '¡ESTE CORTE ES GRATIS!'
        } : {
            activo: false,
            visitasParaPremio: 10,
            descuentoHabitual: 2.00,
            textoDescuento: '¡Cliente habitual! Descuento aplicado.',
            textoPremio: '¡ESTE CORTE ES GRATIS!'
        },
        galeria: [
            'https://images.unsplash.com/photo-1599011176306-4a96f1516d4d?q=85&w=800',
            'https://images.unsplash.com/photo-1585747860715-2ba37e788b70?q=85&w=800',
            'https://images.unsplash.com/photo-1621605815971-fbc98d665033?q=85&w=800'
        ],
        faqs: [
            { pregunta: '¿Cómo reservo una cita?', respuesta: 'Usa el botón de Telegram para reservar tu cita de forma rápida y sencilla.' },
            { pregunta: '¿Puedo cancelar mi cita?', respuesta: 'Sí, puedes cancelar hasta 24 horas antes de tu cita.' }
        ],
        api: {
            endpoint: '',
            username: tenantId || 'demo'
        },
        reservas: {
            anticipacionMaxima: 30,
            intervaloSlots: 30,
            horasCancelacion: 24,
            permiteCancelar: true
        },
        legal: {
            textoFooter: `© 2026 ${backendConfig.nombre || 'Barbería Demo'}`
        },
        barberos: [],
        metodosPago: {},
        promociones: []
    };
}
